/**
 * v4 "My Business" - annual financial dashboard.
 *
 * Headline figures, top lists and benchmarks are all annual (current FY vs
 * previous FY). The quarterly trend chart + GST-per-quarter chart provide
 * the only sub-annual cut, because BAS is the only sub-annual cadence
 * small Aus businesses actually reconcile against.
 *
 * Sections:
 *   1. Headline KPI strip (revenue / expenses / profit / margin - annual)
 *   2. BAS countdown + GST card (current quarter)
 *   3. Owed to me (aged AR)
 *   4. I owe (aged AP, ATO vs other)
 *   5. Quarterly revenue & profit trend (last 8 quarters)
 *   6. GST collected / paid per quarter
 *   7. Top customers / top suppliers / top expenses (this FY)
 *   8. How you compare (industry benchmarks, this FY)
 *   9. Worth discussing (deduction tips)
 *
 * Current-FY P&L numbers are badged "Draft" - they reflect the live Xero
 * books and may shift when the accountant finalises the year.
 */

import { useQuery } from '@tanstack/react-query';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip,
  CartesianGrid,
} from 'recharts';
import {
  AlertCircle, Loader2, TrendingUp, TrendingDown, Lightbulb, Calendar,
  ArrowDownToLine, ArrowUpFromLine, Receipt, Building2, Users, DollarSign,
  BadgeAlert, BarChart3 as BarIcon, ExternalLink,
} from 'lucide-react';
import { useV4 } from './i18n';
import { BenchmarkRangeBar } from '@/components/shared/BenchmarkRangeBar';
import {
  getFinancials, getDeadlines, ClientFinancials, AgedBuckets,
  ClientDeadline,
} from '@/services/api/client-auth';
import { cn } from '@/lib/utils';

const CHART_COLORS = ['#35AD88', '#6366F1', '#F59E0B', '#EC4899', '#06B6D4', '#8B5CF6', '#10B981'];

function fmtFullCurrency(n: number): string {
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 }).format(Math.round(n));
}

function fmtCompact(n: number): string {
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}m`;
  if (abs >= 10_000)    return `${sign}$${(abs / 1_000).toFixed(0)}k`;
  if (abs >= 1_000)     return `${sign}$${(abs / 1_000).toFixed(1)}k`;
  return `${sign}$${Math.round(abs)}`;
}

export default function MoneyPage() {
  const { accent } = useV4();

  const { data: fin, isLoading, error, isFetching } = useQuery<ClientFinancials>({
    queryKey: ['client-financials-v2', 'year'],
    queryFn: () => getFinancials('year'),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const { data: deadlinesData } = useQuery({
    queryKey: ['client-deadlines'],
    queryFn: getDeadlines,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });

  return (
    <div className="space-y-3 pb-12">
      <header className="pt-1">
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Money</p>
      </header>

      {isLoading && (
        <div className="flex flex-col items-center justify-center min-h-[40vh] text-muted-foreground">
          <Loader2 className="w-6 h-6 animate-spin mb-3" />
          <p className="text-sm">Loading your business…</p>
        </div>
      )}

      {error && !isLoading && (
        <div className="flex flex-col items-center justify-center min-h-[40vh] text-muted-foreground px-6">
          <AlertCircle className="w-8 h-8 mb-3 text-rose-500" />
          <p className="text-sm text-center">Couldn't load your financials right now.</p>
        </div>
      )}

      {fin && !fin.connected && (
        <section className="rounded-2xl border border-dashed bg-card/50 p-6 text-center">
          <DollarSign className="w-8 h-8 mx-auto mb-3 text-muted-foreground/60" />
          <h2 className="text-sm font-semibold mb-1.5">Live financials - not yet connected</h2>
          <p className="text-xs text-muted-foreground leading-relaxed max-w-sm mx-auto">
            {fin.message || 'Ask your accountant to connect your accounting software so live revenue and expenses appear here.'}
          </p>
        </section>
      )}

      {fin && fin.connected && (
        <>
          {fin.fallbackNote && (
            <div className="rounded-lg border border-amber-200 dark:border-amber-900/60 bg-amber-50/70 dark:bg-amber-950/30 px-3 py-2.5 text-xs text-amber-900 dark:text-amber-200">
              {fin.fallbackNote}
            </div>
          )}

          {/* 1. Headline KPI strip */}
          {fin.current && fin.deltas && (
            <section className={cn('rounded-2xl border bg-card p-5 shadow-sm transition-opacity', isFetching && 'opacity-60')}>
              <div className="flex items-center justify-between">
                <p className="text-[10px] uppercase font-semibold tracking-wider text-muted-foreground">{fin.periodLabel}</p>
                {fin.isDraft && (
                  <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300">
                    Draft
                  </span>
                )}
              </div>
              <p className="text-[10px] uppercase font-semibold tracking-wider text-muted-foreground mt-3">Revenue</p>
              <p className="text-3xl font-bold leading-tight">{fmtFullCurrency(fin.current.revenue)}</p>
              {fin.deltas.revenuePct != null && (
                <p className={cn('text-xs font-semibold mt-1.5 flex items-center gap-1',
                  fin.deltas.revenuePct >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400')}>
                  {fin.deltas.revenuePct >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                  {fin.deltas.revenuePct >= 0 ? '+' : ''}{fin.deltas.revenuePct}% <span className="text-muted-foreground font-normal">vs last FY</span>
                </p>
              )}

              <div className="grid grid-cols-3 gap-3 mt-5 pt-5 border-t">
                <SubKpi label="Expenses" value={fmtFullCurrency(fin.current.expenses)} />
                <SubKpi label="Profit"   value={fmtFullCurrency(fin.current.profit)} />
                <SubKpi label="Margin"   value={`${fin.current.marginPct.toFixed(1)}%`} positive={fin.current.marginPct > 0} />
              </div>

              {fin.isDraft && (
                <p className="text-[10px] text-muted-foreground italic mt-3 leading-relaxed">
                  These figures are from your live accounting books and may change when your accountant finalises the year.
                </p>
              )}
            </section>
          )}

          {/* 2. BAS countdown + GST */}
          {fin.bas && <BasCard bas={fin.bas} accent={accent.primary} />}

          {/* 2b. Upcoming compliance deadlines */}
          {deadlinesData && deadlinesData.deadlines.length > 0 && (
            <DeadlinesCard deadlines={deadlinesData.deadlines} />
          )}

          {/* 3. Owed to me */}
          {fin.receivables && fin.receivables.total > 0 && (
            <ReceivablesCard data={fin.receivables} accent={accent.primary} />
          )}

          {/* 4. I owe */}
          {fin.payables && fin.payables.total > 0 && (
            <PayablesCard data={fin.payables} />
          )}

          {/* 5. Quarterly revenue & profit trend */}
          {fin.quarterly && fin.quarterly.length > 0 && (
            <section className="rounded-2xl border bg-card p-5 shadow-sm">
              <h2 className="text-sm font-semibold flex items-center gap-1.5">
                <BarIcon className="w-3.5 h-3.5 text-[var(--v4-accent-text)]" /> Revenue & profit
              </h2>
              <p className="text-[10px] text-muted-foreground mt-0.5 mb-3">Last 8 BAS quarters</p>
              <div className="h-48 -mx-2">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={fin.quarterly} margin={{ left: 0, right: 12, top: 8, bottom: 4 }}>
                    <CartesianGrid stroke="var(--border)" strokeOpacity={0.25} vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 10, fill: 'currentColor' }} axisLine={false} tickLine={false} />
                    <YAxis tickFormatter={fmtCompact} tick={{ fontSize: 10, fill: 'currentColor' }} axisLine={false} tickLine={false} width={36} />
                    <Tooltip
                      contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 10, fontSize: 12 }}
                      formatter={(v: number, name: string) => [fmtFullCurrency(v), name]}
                    />
                    <Line type="monotone" dataKey="revenue" name="Revenue" stroke={accent.primary} strokeWidth={2.5} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="profit"  name="Profit"  stroke="#94A3B8" strokeWidth={2}   strokeDasharray="4 3" dot={{ r: 2 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="flex items-center gap-4 mt-2 text-[11px] text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: accent.primary }} /> Revenue
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="inline-block w-2.5 h-2.5 rounded-full bg-slate-400" /> Profit
                </span>
              </div>
            </section>
          )}

          {/* 6. GST collected vs paid per quarter */}
          {fin.quarterly && fin.quarterly.some(q => q.gstCollected > 0 || q.gstPaid > 0) && (
            <section className="rounded-2xl border bg-card p-5 shadow-sm">
              <h2 className="text-sm font-semibold flex items-center gap-1.5">
                <Receipt className="w-3.5 h-3.5 text-[var(--v4-accent-text)]" /> GST per quarter
              </h2>
              <p className="text-[10px] text-muted-foreground mt-0.5 mb-3">Collected vs paid + net BAS position</p>
              <div className="h-44 -mx-2">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={fin.quarterly} margin={{ left: 0, right: 12, top: 8, bottom: 4 }}>
                    <CartesianGrid stroke="var(--border)" strokeOpacity={0.25} vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 10, fill: 'currentColor' }} axisLine={false} tickLine={false} />
                    <YAxis tickFormatter={fmtCompact} tick={{ fontSize: 10, fill: 'currentColor' }} axisLine={false} tickLine={false} width={36} />
                    <Tooltip
                      contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 10, fontSize: 12 }}
                      formatter={(v: number, name: string) => [fmtFullCurrency(v), name]}
                    />
                    <Bar dataKey="gstCollected" name="GST collected" fill={accent.primary} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="gstPaid"      name="GST paid"      fill="#94A3B8"      radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
                {fin.quarterly.slice(-3).map(q => (
                  <div key={q.label} className="text-center">
                    <p className="text-[10px] text-muted-foreground">{q.label}</p>
                    <p className={cn('text-sm font-semibold tabular-nums',
                      q.netGstOwed > 0 ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400')}>
                      {q.netGstOwed > 0 ? '+' : ''}{fmtCompact(q.netGstOwed)}
                    </p>
                    <p className="text-[10px] text-muted-foreground">net BAS</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* 7. Top customers + suppliers + expenses */}
          {(fin.topCustomers?.length || fin.topSuppliers?.length || fin.topExpenses?.length) ? (
            <section className="grid gap-3 sm:grid-cols-2">
              {fin.topCustomers && fin.topCustomers.length > 0 && (
                <TopList icon={Users} title="Top customers" subtitle="By revenue this FY" rows={fin.topCustomers} labelKey="contact" />
              )}
              {fin.topSuppliers && fin.topSuppliers.length > 0 && (
                <TopList icon={Building2} title="Top suppliers" subtitle="By spend this FY" rows={fin.topSuppliers} labelKey="contact" />
              )}
              {fin.topExpenses && fin.topExpenses.length > 0 && (
                <TopList icon={ArrowUpFromLine} title="Top expenses" subtitle="This FY" rows={fin.topExpenses} labelKey="category" />
              )}
            </section>
          ) : null}

          {/* 8. How you compare */}
          {fin.comparison && fin.comparison.rows.length > 0 && (
            <section className="rounded-2xl border bg-card p-5 shadow-sm">
              <h2 className="text-sm font-semibold flex items-center gap-1.5">
                <TrendingUp className="w-3.5 h-3.5 text-[var(--v4-accent-text)]" /> How you compare
              </h2>
              <p className="text-[10px] text-muted-foreground mt-0.5 mb-4">
                Versus the typical range for businesses in your industry
              </p>
              <div className="space-y-5">
                {fin.comparison.rows.map(row => (
                  <BenchmarkRangeBar key={row.label} row={row} />
                ))}
              </div>
              {fin.comparison.source && (
                <a
                  href={fin.comparison.source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 inline-flex items-center gap-1 text-[10px] text-muted-foreground hover:text-[var(--v4-accent-text)] underline underline-offset-2"
                >
                  Source: {fin.comparison.source.title}
                  <ExternalLink className="w-2.5 h-2.5" />
                </a>
              )}
            </section>
          )}

          {/* 9. Worth discussing */}
          {fin.tips && fin.tips.length > 0 && (
            <section className="rounded-2xl border bg-amber-50/60 dark:bg-amber-950/20 border-amber-200/70 dark:border-amber-900/40 p-5">
              <h2 className="text-xs font-bold flex items-center gap-1.5 uppercase tracking-wider text-amber-900 dark:text-amber-200">
                <Lightbulb className="w-3.5 h-3.5" /> Worth discussing
              </h2>
              <p className="text-[10px] text-amber-800/80 dark:text-amber-300/80 mt-0.5 mb-3">
                Possible deductions based on your industry
              </p>
              <div className="space-y-2.5">
                {fin.tips.map((tip, i) => (
                  <div key={i} className="rounded-lg bg-background border border-amber-100/80 dark:border-amber-900/40 p-3">
                    <p className="text-xs font-semibold">{tip.title}</p>
                    <p className="text-[11px] text-muted-foreground leading-relaxed mt-1">{tip.body}</p>
                  </div>
                ))}
              </div>
              <p className="text-[10px] italic text-amber-800/80 dark:text-amber-300/80 mt-3 leading-relaxed">
                Tips are general guidance. Your accountant can confirm what applies to you.
              </p>
            </section>
          )}

          <p className="text-center text-[10px] uppercase font-medium tracking-wider text-muted-foreground pt-1">
            Live from {fin.xeroOrgName} · {fin.asOf ? new Date(fin.asOf).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }) : 'today'}
          </p>
        </>
      )}
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────────────

/**
 * Deadlines - compliance calendar for this client. Shows the next ~5
 * upcoming items (plus any overdue) with a coloured status pill. The
 * full ATO calendar lives on the firm side; this is the read-only client
 * view scoped to their entity type.
 */
function DeadlinesCard({ deadlines }: { deadlines: ClientDeadline[] }) {
  const overdue = deadlines.filter(d => d.status === 'overdue');
  const dueSoon = deadlines.filter(d => d.status === 'due_soon');
  const upcoming = deadlines.filter(d => d.status === 'upcoming');
  // Show overdue + due_soon in full, then top up with upcoming to ~6 rows.
  const visible = [...overdue, ...dueSoon, ...upcoming].slice(0, 6);
  const hiddenCount = deadlines.length - visible.length;

  return (
    <section className="rounded-2xl border bg-card p-5 shadow-sm">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-sm font-semibold flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5 text-[var(--v4-accent-text)]" /> Upcoming deadlines
          </h2>
          <p className="text-[10px] text-muted-foreground mt-0.5">
            ATO lodgement & payment calendar
          </p>
        </div>
        {overdue.length > 0 && (
          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300 flex items-center gap-1">
            <BadgeAlert className="w-2.5 h-2.5" /> {overdue.length} overdue
          </span>
        )}
      </div>

      <ul className="mt-4 space-y-2.5">
        {visible.map(d => (
          <DeadlineRow key={d.id} d={d} />
        ))}
      </ul>

      {hiddenCount > 0 && (
        <p className="mt-3 text-[10px] text-muted-foreground text-center">
          + {hiddenCount} more upcoming
        </p>
      )}

      <p className="text-[10px] italic text-muted-foreground mt-3 leading-relaxed">
        Your accountant manages these lodgements. Reach out via chat if you have questions.
      </p>
    </section>
  );
}

function DeadlineRow({ d }: { d: ClientDeadline }) {
  const due = new Date(d.dueDate);
  const dueLabel = due.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });

  const isOverdue = d.status === 'overdue';
  const isDueSoon = d.status === 'due_soon';

  const countdown = isOverdue
    ? `${Math.abs(d.daysUntil)}d overdue`
    : d.daysUntil === 0
      ? 'Due today'
      : `${d.daysUntil}d to go`;

  const pillClass = isOverdue
    ? 'bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300'
    : isDueSoon
      ? 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300'
      : 'bg-muted text-muted-foreground';

  return (
    <li className="flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium truncate">{shortenTitle(d.title)}</p>
        <p className="text-[10px] text-muted-foreground">Due {dueLabel}</p>
      </div>
      <span className={cn('text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md whitespace-nowrap', pillClass)}>
        {countdown}
      </span>
    </li>
  );
}

/**
 * Trim the ATO calendar title for the client view - they don't need the
 * "FY2025/2026" suffix the firm uses for filing reference.
 */
function shortenTitle(title: string): string {
  return title.replace(/\s+FY\d{4}\/\d{4}\s*$/, '').trim();
}

function SubKpi({ label, value, positive }: { label: string; value: string; positive?: boolean }) {
  return (
    <div>
      <p className="text-[10px] uppercase font-semibold tracking-wider text-muted-foreground">{label}</p>
      <p className={cn('text-lg font-bold mt-1 leading-tight', positive ? 'text-emerald-600 dark:text-emerald-400' : '')}>
        {value}
      </p>
    </div>
  );
}

function BasCard({ bas, accent }: { bas: NonNullable<ClientFinancials['bas']>; accent: string }) {
  const dueLabel = bas.daysUntilDue >= 0
    ? `${bas.daysUntilDue} day${bas.daysUntilDue === 1 ? '' : 's'} until BAS`
    : `${Math.abs(bas.daysUntilDue)} day${Math.abs(bas.daysUntilDue) === 1 ? '' : 's'} overdue`;
  const dueColor = bas.daysUntilDue < 0
    ? 'text-rose-600 dark:text-rose-400'
    : bas.daysUntilDue <= 14
      ? 'text-amber-600 dark:text-amber-400'
      : 'text-emerald-600 dark:text-emerald-400';
  const netOwed = bas.netGstOwed;
  return (
    <section className="rounded-2xl border bg-card p-5 shadow-sm">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-sm font-semibold flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5 text-[var(--v4-accent-text)]" /> BAS this quarter
          </h2>
          <p className="text-[10px] text-muted-foreground mt-0.5">{bas.quarterLabel}</p>
        </div>
        <div className="text-right">
          <p className={cn('text-xs font-semibold', dueColor)}>{dueLabel}</p>
          <p className="text-[10px] text-muted-foreground">
            Due {new Date(bas.dueDate).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}
          </p>
        </div>
      </div>

      <div className="mt-4 rounded-xl bg-[var(--v4-accent-tint)] p-4">
        <p className="text-[10px] uppercase font-semibold tracking-wider text-muted-foreground">
          {netOwed >= 0 ? 'You\'ll owe ATO (estimate)' : 'ATO will refund (estimate)'}
        </p>
        <p className="text-3xl font-bold leading-tight mt-1" style={{ color: accent }}>
          {fmtFullCurrency(Math.abs(netOwed))}
        </p>
        <p className="text-[10px] text-muted-foreground italic mt-2 leading-relaxed">
          Estimated GST position. Your accountant will finalise the actual BAS figure.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 mt-4">
        <div>
          <p className="text-[10px] uppercase font-semibold tracking-wider text-muted-foreground flex items-center gap-1">
            <ArrowDownToLine className="w-3 h-3" /> Money in
          </p>
          <p className="text-base font-bold mt-0.5">{fmtFullCurrency(bas.moneyIn)}</p>
          <p className="text-[10px] text-muted-foreground">GST collected: {fmtFullCurrency(bas.gstCollected)}</p>
        </div>
        <div>
          <p className="text-[10px] uppercase font-semibold tracking-wider text-muted-foreground flex items-center gap-1">
            <ArrowUpFromLine className="w-3 h-3" /> Money out
          </p>
          <p className="text-base font-bold mt-0.5">{fmtFullCurrency(bas.moneyOut)}</p>
          <p className="text-[10px] text-muted-foreground">GST paid: {fmtFullCurrency(bas.gstPaid)}</p>
        </div>
      </div>
    </section>
  );
}

function AgedBucketsBar({ buckets, accent }: { buckets: AgedBuckets; accent: string }) {
  const total = buckets.current + buckets.d30 + buckets.d60 + buckets.d90 + buckets.d90plus;
  if (total === 0) return null;
  const pct = (n: number) => (n / total) * 100;
  return (
    <div className="mt-3">
      <div className="flex h-2 rounded-full overflow-hidden">
        <div style={{ width: `${pct(buckets.current)}%`, background: accent }} />
        <div style={{ width: `${pct(buckets.d30)}%`,    background: '#F59E0B' }} />
        <div style={{ width: `${pct(buckets.d60)}%`,    background: '#F97316' }} />
        <div style={{ width: `${pct(buckets.d90)}%`,    background: '#EF4444' }} />
        <div style={{ width: `${pct(buckets.d90plus)}%`, background: '#B91C1C' }} />
      </div>
      <div className="grid grid-cols-5 gap-1 mt-2 text-[9px] text-center text-muted-foreground">
        <BucketCell label="Current"    amount={buckets.current} dot={accent} />
        <BucketCell label="1-30d"      amount={buckets.d30}     dot="#F59E0B" />
        <BucketCell label="31-60d"     amount={buckets.d60}     dot="#F97316" />
        <BucketCell label="61-90d"     amount={buckets.d90}     dot="#EF4444" />
        <BucketCell label="90+ d"      amount={buckets.d90plus} dot="#B91C1C" />
      </div>
    </div>
  );
}

function BucketCell({ label, amount, dot }: { label: string; amount: number; dot: string }) {
  return (
    <div className="flex flex-col items-center gap-0.5">
      <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: dot }} />
      <span className="font-semibold uppercase tracking-wider">{label}</span>
      <span className="text-foreground/80 text-[10px] tabular-nums">{fmtCompact(amount)}</span>
    </div>
  );
}

function ReceivablesCard({ data, accent }: { data: NonNullable<ClientFinancials['receivables']>; accent: string }) {
  return (
    <section className="rounded-2xl border bg-card p-5 shadow-sm">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-sm font-semibold flex items-center gap-1.5">
            <ArrowDownToLine className="w-3.5 h-3.5 text-[var(--v4-accent-text)]" /> Owed to you
          </h2>
          <p className="text-[10px] text-muted-foreground mt-0.5">Outstanding sales invoices</p>
        </div>
        {data.overdueCount > 0 && (
          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300 flex items-center gap-1">
            <BadgeAlert className="w-2.5 h-2.5" /> {data.overdueCount} overdue
          </span>
        )}
      </div>
      <p className="text-2xl font-bold mt-3 leading-tight">{fmtFullCurrency(data.total)}</p>

      <AgedBucketsBar buckets={data.buckets} accent={accent} />

      {data.topOutstanding.length > 0 && (
        <ul className="mt-4 space-y-2">
          {data.topOutstanding.map((inv, i) => (
            <li key={i} className="flex items-center justify-between gap-2 text-xs">
              <div className="min-w-0 flex-1">
                <p className="font-medium truncate">{inv.contact}</p>
                <p className="text-[10px] text-muted-foreground">
                  {inv.invoiceNumber ? `${inv.invoiceNumber} · ` : ''}
                  {inv.daysOverdue > 0 ? `${inv.daysOverdue}d overdue` : 'Due soon'}
                </p>
              </div>
              <span className="font-semibold tabular-nums">{fmtFullCurrency(inv.amount)}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function PayablesCard({ data }: { data: NonNullable<ClientFinancials['payables']> }) {
  const hasAto = data.ato > 0;
  return (
    <section className="rounded-2xl border bg-card p-5 shadow-sm">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-sm font-semibold flex items-center gap-1.5">
            <ArrowUpFromLine className="w-3.5 h-3.5 text-[var(--v4-accent-text)]" /> You owe
          </h2>
          <p className="text-[10px] text-muted-foreground mt-0.5">Outstanding bills</p>
        </div>
        {data.overdueCount > 0 && (
          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300 flex items-center gap-1">
            <BadgeAlert className="w-2.5 h-2.5" /> {data.overdueCount} overdue
          </span>
        )}
      </div>
      <p className="text-2xl font-bold mt-3 leading-tight">{fmtFullCurrency(data.total)}</p>

      {hasAto && (
        <div className="grid grid-cols-2 gap-3 mt-3">
          <div className="rounded-lg border border-rose-200 dark:border-rose-900/60 bg-rose-50/60 dark:bg-rose-950/30 p-2.5">
            <p className="text-[10px] uppercase font-semibold tracking-wider text-rose-700 dark:text-rose-300">ATO</p>
            <p className="text-base font-bold leading-tight mt-0.5">{fmtFullCurrency(data.ato)}</p>
          </div>
          <div className="rounded-lg border border-border bg-muted/40 p-2.5">
            <p className="text-[10px] uppercase font-semibold tracking-wider text-muted-foreground">Other suppliers</p>
            <p className="text-base font-bold leading-tight mt-0.5">{fmtFullCurrency(data.other)}</p>
          </div>
        </div>
      )}

      <AgedBucketsBar buckets={data.buckets} accent="#F43F5E" />

      {data.topOutstanding.length > 0 && (
        <ul className="mt-4 space-y-2">
          {data.topOutstanding.map((b, i) => (
            <li key={i} className="flex items-center justify-between gap-2 text-xs">
              <div className="min-w-0 flex-1">
                <p className="font-medium truncate flex items-center gap-1.5">
                  {b.contact}
                  {b.isAto && (
                    <span className="text-[8px] font-bold uppercase px-1 py-0 rounded bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300">ATO</span>
                  )}
                </p>
                <p className="text-[10px] text-muted-foreground">
                  {b.daysOverdue > 0 ? `${b.daysOverdue}d overdue` : 'Due soon'}
                </p>
              </div>
              <span className="font-semibold tabular-nums">{fmtFullCurrency(b.amount)}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function TopList({
  icon: Icon, title, subtitle, rows, labelKey,
}: {
  icon: any; title: string; subtitle: string;
  rows: Array<Record<string, any>>; labelKey: 'contact' | 'category';
}) {
  const max = Math.max(...rows.map(r => r.amount), 1);
  return (
    <div className="rounded-2xl border bg-card p-4 shadow-sm">
      <h2 className="text-xs font-semibold flex items-center gap-1.5">
        <Icon className="w-3 h-3 text-[var(--v4-accent-text)]" /> {title}
      </h2>
      <p className="text-[10px] text-muted-foreground mt-0.5 mb-2.5">{subtitle}</p>
      <ul className="space-y-1.5">
        {rows.slice(0, 5).map((r, i) => (
          <li key={i} className="text-[11px]">
            <div className="flex justify-between items-center gap-2">
              <span className="font-medium truncate">{r[labelKey]}</span>
              <span className="font-semibold tabular-nums whitespace-nowrap">{fmtCompact(r.amount)}</span>
            </div>
            <div className="mt-0.5 h-1 rounded-full bg-muted overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${(r.amount / max) * 100}%`, background: CHART_COLORS[i % CHART_COLORS.length] }} />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

// The benchmark range-band row now lives in the shared BenchmarkRangeBar
// component (used by both this client view and the firm-side compliance
// checker) so the two never diverge.
